/* WorkTriggerTargetsTabComponent.js */

"use strict";
const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./WorkTriggerTargetsTabRenderer");
const PagingTargetDeviceFeed = require("./PagingTargetDeviceFeed");
const PagingDeviceFeed = require("./PagingDeviceFeed");
const {
    DEVICE_MANAGEMENT_STATES,
    LICENSE_STATUS,
    DEVICE_CAPTURE_STATUSES,
    AUDIT_STATUSES,
    DEVICE_ENVIRONMENTS,
} = require("../../../../../../lmrbcomponents/deviceexplorer/views/device_grid_view/deviceConstants");

class WorkTriggerTargetsTabComponent extends EventEmitter {
    constructor(workTriggerDetailsView) {
        super();
        this._workTriggerDetailsView = workTriggerDetailsView;
        this._workTriggerDetailsView._targetViewOpen = true;
        this._gameEditor = workTriggerDetailsView.getGameEditor();
        this._sitePaths = this._workTriggerDetailsView._sitePaths;
        this._nodeProps = this._workTriggerDetailsView._nodeProps;
        this._CustomFieldsEntityClass = _.get(
            this._workTriggerDetailsView,
            "_customFieldEntityDef"
        );
        this._customFieldsWithInherited = {};
        this._customFieldsEntityInstance = {};

        this._gridKey = "schedule_targets";

        this._method = _.get(this._workTriggerDetailsView._triggerType.getWorkTarget(), "method");
        this._filter = {};
        this._initiateFeeds();

        this._renderer = new Renderer(this);
        this._registerListeners();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {
        this._workTriggerDetailsView.on(
            "Shutdown",
            (this._onShutdown = this._onShutdown.bind(this))
        );
        this._workTriggerDetailsView.on(
            "WorkTypeChanged",
            (this._onWorkTypeChanged = this._onWorkTypeChanged.bind(this))
        );
        this._workTriggerDetailsView.on(
            "WorkTargetChanged",
            (this._updateTheTargetsControl = this._updateTheTargetsControl.bind(this))
        );
        this._workTriggerDetailsView.on(
            "BackFromTarget",
            (this._backButtonClicked = this._backButtonClicked.bind(this))
        );
    }

    _unregisterListeners() {
        this._workTriggerDetailsView.removeListener("Shutdown", this._onShutdown);
        this._workTriggerDetailsView.removeListener("WorkTypeChanged", this._onWorkTypeChanged);
        this._workTriggerDetailsView.removeListener(
            "WorkTargetChanged",
            this._updateTheTargetsControl
        );
        this._workTriggerDetailsView.removeListener("BackFromTarget", this._backButtonClicked);
    }

    shutdown() {
        return Promise.all([
            this._availableDevicesPagingFeed
                ? this._availableDevicesPagingFeed.shutdown()
                : Promise.resolve(),
            this._selectedDevicesPagingFeed.shutdown(),
        ])
            .then(() => {
                this.emit("Shutdown");
                this._unregisterListeners();
                this._workTriggerDetailsView._targetViewOpen = undefined;
                this._workTriggerDetailsView = undefined;
                this._availableDevicesPagingFeed = undefined;
                this._selectedDevicesPagingFeed = undefined;
            })
            .catch((err) => {
                this.logger.warn({error: err}, "WorkTriggerTargetsTabComponent.shutdown() error");
                //console.error(err);
            });
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    get gameEditor() {
        return this._gameEditor;
    }

    get workTriggerDetailsView() {
        return this._workTriggerDetailsView;
    }

    get availColumnConfig() {
        return {
            name: {
                label: "Name", //`Selected ${this._workTriggerDetailsView.getTargetName()}s`,
                entityColumn: "name",
                width: 140,
                priority: 1,
                sortable: false,
            },
            detectedHostName: {
                label: "Hostname",
                shown: true,
                width: 100,
                priority: 2,
            },
            ip: {
                label: "IP Address",
                path: "connectionInformation.ip",
                shown: true,
                width: 100,
                priority: 3,
            },
            detectedSku: {
                label: "SKUs",
                shown: true,
                width: 100,
                priority: 4,
            },
            detectedOs: {
                label: "OS",
                shown: true,
                priority: 5,
                width: 100,
                options: Object.entries(this._workTriggerDetailsView._osMap).map(([, title]) => ({
                    name: title,
                    title,
                })),
            },
            detectedSoftwareVersion: {
                label: "OS Version",
                shown: true,
                priority: 6,
                width: 95,
            },
            nodeId: {
                label: "nodeid",
                shown: false,
                priority: 7,
            },
            siteCodeName: {
                label: "Site Code Name",
                entityColumn: "siteCodeName",
                width: 120,
            },
        };
    }

    get selColumnConfig() {
        return {
            name: {
                label: "Name",
                entityColumn: "name",
                width: 140,
                priority: 1,
                sortable: false,
            },
            detectedHostName: {
                label: "Hostname",
                shown: true,
                width: 100,
                priority: 2,
            },
            ip: {
                label: "IP Address",
                path: "connectionInformation.ip",
                shown: true,
                width: 100,
                priority: 3,
            },
            detectedSku: {
                label: "SKUs",
                shown: true,
                width: 100,
                priority: 4,
            },
            detectedOs: {
                label: "OS",
                shown: true,
                priority: 5,
                width: 80,
                options: Object.entries(this._workTriggerDetailsView._osMap).map(([, title]) => ({
                    name: title,
                    title,
                })),
            },
            detectedSoftwareVersion: {
                label: "OS Version",
                shown: true,
                priority: 6,
                width: 95,
            },
            nodeId: {
                label: "nodeid",
                shown: false,
                priority: 7,
            },
            siteCodeName: {
                label: "Site Code Name",
                entityColumn: "siteCodeName",
                width: 120,
            },
        };
    }

    get columnConfigFilter() {
        if (this.columnsFilter) {
            return this.columnsFilter;
        }
        this._deviceEntityDef = this._workTriggerDetailsView.getTargetEntityClass();
        const vendorsMap = this._workTriggerDetailsView._vendorMap,
            accessStatusLabels = this._deviceEntityDef.getAccessStatusLabels();

        //creating a new static DEVICE_TYPES list to pass to filter dropdown for types (via options)
        //TODO: create a non-redundant list directly in the filter
        let typeBaseGroupMap = this._workTriggerDetailsView._typeBaseMap;
        let optionFilterList = {};
        _.sortBy(Object.keys(typeBaseGroupMap)).map(function (key) {
            optionFilterList[key] = {name: typeBaseGroupMap[key], title: typeBaseGroupMap[key]};
        });
        const serversMap = _.get(this._parentView, "_gameEditor.osImageServerList", []).map(
            ({name}) => ({
                title: name,
                name,
            })
        );
        const optionMapAccessStatus = {};
        Object.keys(accessStatusLabels).forEach(
            (accessLabel) =>
                (optionMapAccessStatus[accessLabel] = {
                    name: accessLabel,
                    title: accessStatusLabels[accessLabel],
                })
        );

        const optionMapSitePaths = this._sitePaths.map(({id, path}) => ({
            [id]: path || "/",
        }));

        this.columnsFilter = {
            detectedTypeBase: {
                label: "Type",
                entityColumn: true,
                width: 45,
                resizable: false,
                priority: 1,
                options: optionFilterList,
                dataType: "enum",
                groupable: true,
                shown: true,
            },
            name: {
                label: "Name",
                width: 220,
                dataType: "string",
                shown: true,
                priority: 2,
            },
            description: {
                label: "Description",
                shown: true,
                dataType: "string",
                width: 300,
                priority: 4,
            },
            managementState: {
                label: "Management State",
                groupable: true,
                shown: false,
                options: DEVICE_MANAGEMENT_STATES,
                dataType: "enum",
                width: 140,
                priority: 5,
            },
            environment: {
                label: "Environment",
                shown: true,
                options: DEVICE_ENVIRONMENTS,
                width: 140,
                dataType: "enum",
                priority: 6,
            },
            licensed: {
                label: "Licensed",
                groupable: true,
                shown: false,
                options: LICENSE_STATUS,
                dataType: "boolean",
                width: 140,
                priority: 6,
            },
            ip: {
                label: "IP Address",
                path: "connectionInformation.ip",
                shown: true,
                dataType: "string",
                width: 140,
                priority: 3,
            },
            detectedVendor: {
                label: "Vendor",
                groupable: true,
                shown: true,
                priority: 5,
                dataType: "enum",
                width: 140,
                options: Object.entries(vendorsMap).map(([, title]) => ({name: title, title})),
            },
            detectedHostName: {
                label: "Hostname",
                shown: true,
                dataType: "string",
                priority: 8,
                width: 140,
            },
            detectedOs: {
                label: "OS",
                shown: true,
                priority: 9,
                width: 140,
                dataType: "enum",
                groupable: true,
                options: Object.entries(this._workTriggerDetailsView._osMap).map(([, title]) => ({
                    name: title,
                    title,
                })),
            },
            detectedSoftwareVersion: {
                label: "OS Version",
                shown: true,
                priority: 10,
                dataType: "string",
                width: 140,
                groupable: true,
            },
            detectedSerialNumber: {
                // check name
                label: "Serial Number",
                shown: true,
                groupable: true,
                dataType: "string",
                width: 140,
                priority: 11,
            },
            detectedSku: {
                label: "SKUs",
                shown: true,
                groupable: true,
                dataType: "string",
                width: 140,
                priority: 12,
            },
            detectedUptime: {
                label: "Up Time",
                shown: false,
                width: 120,
                dataType: "string",
                priority: 13,
            },
            elementCount: {
                label: "Elements",
                width: 110,
                dataType: "number",
                shown: false,
                priority: 14,
            },
            fileServerName: {
                // check field
                label: "File Server",
                shown: false,
                options: serversMap,
                dataType: "enum",
                groupable: true,
                priority: 15,
            },
            haPairName: {
                label: "HA Group",
                priority: 18,
                dataType: "string",
            },
            osUpgradedBy: {
                label: "OSM Action By",
                priority: 19,
                dataType: "string",
                groupable: true,
            },
            osUpgradedOn: {
                label: "OSM Action On",
                shown: false,
                priority: 20,
                dataType: "string",
                groupable: true,
            },
            osUpgradeError: {
                label: "OSM Action Error",
                shown: false,
                priority: 21,
                dataType: "string",
                groupable: false,
            },
            osUpgradePlanAction: {
                label: "OS Plan Action",
                shown: false,
                priority: 24,
                dataType: "string",
                groupable: true,
            },
            osUpgradePlanId: {
                label: "OS Plan ID ",
                shown: false,
                priority: 22,
                dataType: "string",
                groupable: false,
            },
            osUpgradePlanName: {
                label: "OS Plan Name",
                shown: false,
                priority: 23,
                dataType: "string",
                groupable: true,
            },
            osUpgradeStatus: {
                label: "OSM Action Status",
                shown: true,
                priority: 18,
                dataType: "enum",
                options: this._getOsPlanDataForAdvancedFilter(
                    this._deviceEntityDef.getOsUpgradeStatusValues()
                ),
                groupable: true,
            },
            useIssu: {
                label: "Non-disruptive",
                width: 80,
                dataType: "boolean",
                description: "Require non-disruptive upgrade",
                shown: false,
                priority: 18,
            },
            vrf: {
                label: "VRF",
                shown: false,
                dataType: "string",
                priority: 16,
            },
            zoneName: {
                label: "Zone",
                shown: false,
                dataType: "string",
                groupable: true,
                width: 160,
                priority: 18,
            },
            configuredInfo: {
                label: "Configured Info",
                width: 160,
                shown: false,
                dataType: "string",
                groupable: true,
                priority: 19,
            },
            configuredType: {
                label: "Configured Type",
                width: 120,
                dataType: "string",
                shown: false,
                groupable: true,
                priority: 19,
            },
            configuredBy: {
                label: "Configured By",
                width: 120,
                shown: false,
                dataType: "string",
                groupable: true,
                priority: 19,
            },
            configuredOn: {
                label: "Configured On",
                width: 160,
                dataType: "string",
                shown: false,
                groupable: false,
                priority: 19,
            },
            accessStatus: {
                label: "Access Status",
                shown: false,
                priority: 26,
                dataType: "enum",
                groupable: true,
                options: optionMapAccessStatus,
            },
            latestHaveIChangedLastChecked: {
                label: "Captured On",
                shown: false,
                dataType: "string",
                priority: 27,
            },
            latestHaveIChanged: {
                label: "Captured Status",
                priority: 28,
                dataType: "enum",
                options: DEVICE_CAPTURE_STATUSES,
                groupable: true,
                shown: true,
            },
            capturedBy: {
                label: "Captured By",
                priority: 29,
                dataType: "string",
                width: 120,
            },
            auditCriticalViolationCount: {
                label: "Critical Violations",
                priority: 30,
                dataType: "number",
                shown: true,
            },
            auditMajorViolationCount: {
                label: "Major Violations",
                priority: 31,
                dataType: "number",
                width: 120,
            },
            auditMinorViolationCount: {
                label: "Minor Violations",
                dataType: "number",
                priority: 30,
            },
            auditInfoViolationCount: {
                label: "Informational Violations",
                priority: 31,
                width: 175,
                dataType: "number",
            },
            auditViolationCount: {
                label: "Total Violations",
                priority: 32,
                width: 120,
                groupable: false,
                dataType: "number",
            },
            auditedOn: {
                label: "Audited On",
                dataType: "string",
                priority: 33,
            },
            auditedBy: {
                priority: 34,
                label: "Audited By",
                dataType: "string",
            },
            auditStatus: {
                label: "Audit Status",
                priority: 35,
                options: AUDIT_STATUSES,
                dataType: "enum",
            },
            lastAuditPolicy: {
                label: "Audit Policy",
                priority: 36,
                dataType: "string",
            },
            sitePath: {
                label: "Site Path",
                priority: 37,
            },
            siteName: {
                label: "Site Name",
                priority: 38,
            },
            siteCodeName: {
                label: "Site Code Name",
                priority: 39,
            },
            siteLocationType: {
                label: "Site Type",
                priority: 40,
            },
            allowSiteAutoAssignment: {
                label: "Allow site auto-assignment",
                priority: 41,
            },
            siteId: {
                label: "Site Path List",
                dataType: "pathlist",
                options: optionMapSitePaths,
            },
        };

        return this.columnsFilter;
    }

    get logger() {
        return this._workTriggerDetailsView._logger;
    }

    get workTarget() {
        return this.getWorkTarget();
    }
    getWorkTarget() {
        return this._workTriggerDetailsView.getWorkTarget();
    }
    set workTarget(t) {
        return this.setWorkTarget(t);
    }
    setWorkTarget(t) {
        return this._workTriggerDetailsView.setWorkTarget(t);
    }
    get availableTargetsPagingFeed() {
        return this._availableDevicesPagingFeed;
    }
    get selectedTargetsPagingFeed() {
        return this._selectedDevicesPagingFeed;
    }
    get renderer() {
        return this.getRenderer();
    }
    getRenderer() {
        return this._renderer;
    }
    wasFeedPreviouslyInitialized() {
        return this._availableDevicesPagingFeed.wasPreviouslyInitialized();
    }

    whenFeedReady() {
        const promises = [this._selectedDevicesPagingFeed.whenFeedReady()];

        if (this._method !== "query") {
            promises.push(this._availableDevicesPagingFeed.whenFeedReady());
        }

        return Promise.all(promises);
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onShutdown() {
        this.shutdown();
    }

    _onWorkTypeChanged(newWorkType, oldWorkType) {
        if (
            newWorkType.targetConstruct !== oldWorkType.targetConstruct ||
            newWorkType.targetEntityType !== oldWorkType.targetEntityType
        ) {
            this._createAvailPagingFeed();
            this._createSelPagingFeed();
            this.emit("PagingFeedChange", this._availableDevicesPagingFeed);
        }
    }

    _updateTheTargetsControl(newWorkTarget) {
        if (
            _.get(this._workTriggerDetailsView._triggerType.getWorkTarget(), "category", "") ===
            "construct"
        ) {
            this._setWorkTarget(newWorkTarget.id || newWorkTarget.ids);
        }

        this._setWorkTarget(newWorkTarget.id || newWorkTarget.ids);
    }

    _backButtonClicked() {
        this.emit("BackFromTarget", this);
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    _initiateFeeds() {
        if (this._method === "query") {
            this._filter = _.get(
                this._workTriggerDetailsView._triggerType.getWorkTarget(),
                "query"
            );
            this._createSelPagingFeed(this._filter);
        } else {
            this._createAvailPagingFeed();
            this._createSelPagingFeed();
        }
    }

    _getWorkTargetIds() {
        let workTarget = this._workTriggerDetailsView.getWorkTarget();
        if (workTarget) {
            if (workTarget.id) {
                return [workTarget.id];
            }
            if (workTarget.ids) {
                return workTarget.ids;
            }
        }
        return [];
    }

    _setWorkTarget(ids) {
        // let workTarget =
        //     ids.length === 1
        //         ? { method: "single", id: ids[0] }
        //         : typeof ids === "string"
        //         ? { method: "single", id: ids }
        //         : { method: "multi", ids: ids };

        // if(_.get(this._workTriggerDetailsView._triggerType.getWorkTarget(), 'category', '') === 'construct' ) {
        let workTarget =
            ids.length === 1
                ? Object.assign({}, this._workTriggerDetailsView._triggerType.getWorkTarget(), {
                      id: ids[0],
                  })
                : typeof ids === "string"
                ? Object.assign({}, this._workTriggerDetailsView._triggerType.getWorkTarget(), {
                      id: ids,
                  })
                : Object.assign({}, this._workTriggerDetailsView._triggerType.getWorkTarget(), {
                      ids: ids,
                  });
        // }

        // let workType = this._workTriggerDetailsView.getWorkType();
        // let construct = _.get(workType, "targetConstruct");
        // let entityType =
        //     _.get(workType, "targetEntityType", false) !== false
        //         ? _.get(workType, "targetEntityType")
        //         : this._workTriggerDetailsView.getTargetEntityType();
        // if (construct) {
        //     workTarget.category = "construct";
        //     workTarget.construct = construct;
        // } else if (entityType) {
        //     workTarget.category = "entity";
        //     workTarget.entityType = entityType;
        // }
        this._workTriggerDetailsView.setWorkTarget(workTarget);
    }

    _createAvailPagingFeed() {
        if (this._availableDevicesPagingFeed) {
            return;
        }
        let filter = undefined;
        if (
            _.get(this._workTriggerDetailsView._triggerType.getWorkTarget(), "category", "") ===
            "construct"
        ) {
            filter = {nodeId: {$exists: true}};
        }

        this._availableDevicesPagingFeed = this._createPagingFeed(
            this.availColumnConfig,
            "$nin",
            `${this._gridKey}_available`,
            filter
        );
    }

    _createSelPagingFeed(filter = false) {
        if (this._selectedDevicesPagingFeed) {
            return;
        }

        let devices = [];
        if (this._method === "multi") {
            devices = _.get(this._workTriggerDetailsView._triggerType.getWorkTarget(), "ids", []);
        } else {
            devices = [_.get(this._workTriggerDetailsView._triggerType.getWorkTarget(), "id", [])];
        }
        if (
            _.get(this._workTriggerDetailsView._triggerType.getWorkTarget(), "category", "") ===
            "construct"
        ) {
            filter = {$and: [{nodeId: {$in: devices}}, {nodeId: {$exists: true}}]};
        }

        if (!filter) {
            this._selectedDevicesPagingFeed = this._createPagingFeed(
                this.selColumnConfig,
                "$in",
                `${this._gridKey}_selected`
            );
        } else {
            this._selectedDevicesPagingFeed = this._createPagingDeviceFeed(
                this.selColumnConfig,
                filter,
                `${this._gridKey}_selected`
            );
        }
    }

    /**
     * Merge provided filter with a base condition to exclude IoT devices.
     * Does not mutate the incoming filter.
     * @param {Object|false|undefined} filter
     * @returns {Object} new filter with non-IoT constraint applied
     * @private
     */
    _withNonIotFilter(filter) {
        const nonIot = {deviceRawCategory: {$ne: "iot"}};
        if (!filter || _.isEmpty(filter)) {
            return nonIot;
        }
        if (Array.isArray(filter.$and)) {
            return Object.assign({}, filter, {$and: filter.$and.concat(nonIot)});
        }
        return {$and: [nonIot, filter]};
    }

    _createPagingFeed(columnConfig, mongoIdOperator, feedKey, filter) {
        const _filter = this._withNonIotFilter(filter);
        const obj = {
            activeOrgId: this._gameEditor.getActiveOrgId(),
            entityDef: this._workTriggerDetailsView.getTargetEntityClass(),
            entityType: this._workTriggerDetailsView.getTargetEntityType() || "Device",
            columnConfig: columnConfig,
            mongoIdOperator: mongoIdOperator,
            sort: {name: 1},
            feedKey,
        };

        Object.assign(obj, {filter: _filter});
        return new PagingTargetDeviceFeed(obj);
    }

    _createPagingDeviceFeed(columnConfig, filter, feedKey) {
        const _filter = this._withNonIotFilter(filter);
        return new PagingDeviceFeed({
            feedKey,
            activeOrgId: this._gameEditor.getActiveOrgId(),
            entityDef: this._gameEditor._DeviceEntity,
            columnConfig: columnConfig,
            filter: _filter,
            sort: {name: 1},
            mongoIdOperator: "$in",
        });
    }

    setCustomFieldsEntity(newOrgInstance) {
        this._customFieldsEntityInstance = newOrgInstance;
        return this._customFieldsEntityInstance;
    }

    retrieveCustomFieldsEntity() {
        if (this._customFieldsEntityInstance && this._customFieldsEntityInstance.shutdown) {
            this._customFieldsEntityInstance
                .shutdown()
                .catch((error) => this._logger.warn({error}));
        }
        return this._CustomFieldsEntityClass
            .retrieveCustomFields(this._gameEditor._activeOrgId)
            .then((customFieldsEntity) => {
                this.setCustomFieldsEntity(customFieldsEntity);
                return customFieldsEntity.loadInheritedFields();
            })
            .then((inheritedFields) => {
                Object.assign(
                    this._customFieldsWithInherited,
                    this._customFieldsEntityInstance.fields,
                    inheritedFields
                );
                return this._customFieldsWithInherited;
            });
    }

    _getOsPlanDataForAdvancedFilter(data) {
        let clonedData = _.clone(data);
        let newObj = {};
        for (let key in clonedData) {
            let value = {};
            value[key] = {
                name: key,
                title: clonedData[key],
            };
            newObj = Object.assign(newObj, value);
        }
        return newObj;
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /*******************************************/
}
module.exports = WorkTriggerTargetsTabComponent;
